/**
 *  Pagination — Circular Threads
 *
 *  Replaces the stock Vision theme pagination script. Two changes:
 *
 *  1. HYBRID LOADING. With the "Click to load" setting, the next
 *     AUTO_LOAD_PAGES pages now load automatically as the visitor scrolls,
 *     after which the Load more button takes over again. This keeps the
 *     footer reachable. The "Infinite" setting still means true infinite
 *     scroll, and "Paginated" is untouched.
 *
 *  2. BACK BUTTON. How many pages were loaded, and how far down the visitor
 *     had scrolled, are saved when they leave. Coming back via the browser
 *     back button restores both, instead of dumping them at the top of page 1.
 *     Only runs when the browser has not already restored the page itself.
 *
 *  To change how many pages load automatically, edit AUTO_LOAD_PAGES below.
 */

if (!customElements.get('pagination-theme')) {
  var AUTO_LOAD_PAGES = 2; // pages loaded automatically before the button takes over
  var MAX_RESTORE_PAGES = 10; // safety cap when restoring after a back navigation
  var MAX_EMPTY_PAGES = 3; // pages of all-sold-out products to skip before giving up
  var STATE_TTL = 30 * 60 * 1000; // forget a saved position after 30 minutes
  var STATE_KEY = 'ct:pagination';

  class Pagination extends HTMLElement {
    constructor() {
      super();
      this.i = 2;
      this.autoLoads = 0;
      this.emptyStreak = 0;
      this.restoring = false;
      this.atEnd = false;
    }

    connectedCallback() {
      var base = this;
      var id = this.dataset.section;

      if (this.classList.contains('pagination-type--paginated')) return;

      this.button = this.querySelector('.load-more');
      this.grid = document.querySelector('[data-id="' + id + '"]');
      this.animations_enabled =
        document.body.classList.contains('animations-true') && typeof gsap !== 'undefined';

      if (!this.grid) return;

      this.autoLimit = this.classList.contains('pagination-type--infinite')
        ? Infinity
        : AUTO_LOAD_PAGES;

      this.stateKey = this.buildStateKey();

      if (this.button) {
        this.button.addEventListener('click', function (event) {
          event.preventDefault();
          base.loadProducts();
          this.blur();
        });
      }

      window.addEventListener('pagehide', function () {
        base.saveState();
      });

      var saved = this.readState();
      if (saved) {
        this.restoreFrom(saved);
      } else {
        this.observeForAutoLoad();
      }
    }

    /* ---------- saved position ---------- */

    buildStateKey() {
      var params;
      try {
        params = new URLSearchParams(window.location.search);
        params.delete('page');
      } catch (e) {
        return window.location.pathname;
      }
      var query = params.toString();
      return window.location.pathname + (query ? '?' + query : '');
    }

    saveState() {
      var pages = this.i - 1;
      if (pages < 2) return;
      try {
        sessionStorage.setItem(
          STATE_KEY,
          JSON.stringify({
            key: this.stateKey,
            pages: Math.min(pages, MAX_RESTORE_PAGES),
            scrollY: window.scrollY || window.pageYOffset || 0,
            ts: Date.now()
          })
        );
      } catch (e) {
        /* private browsing, storage full — not worth breaking the page over */
      }
    }

    readState() {
      var navType = '';
      try {
        var entry = performance.getEntriesByType('navigation')[0];
        navType = entry ? entry.type : '';
      } catch (e) {
        return null;
      }
      // Only restore when they actually came back. A fresh visit starts fresh.
      if (navType !== 'back_forward') return null;

      var raw = null;
      try {
        raw = sessionStorage.getItem(STATE_KEY);
      } catch (e) {
        return null;
      }
      if (!raw) return null;

      var state = null;
      try {
        state = JSON.parse(raw);
      } catch (e) {
        return null;
      }
      if (!state || state.key !== this.stateKey) return null;
      if (!state.pages || state.pages < 2) return null;
      if (Date.now() - (state.ts || 0) > STATE_TTL) return null;
      return state;
    }

    restoreFrom(state) {
      var base = this;
      base.restoring = true;
      base.setAttribute('loading', true);
      try {
        history.scrollRestoration = 'manual';
      } catch (e) {}

      var step = function () {
        if (base.i > state.pages || base.atEnd) {
          base.finishRestore(state);
          return;
        }
        var url = window.location.pathname + base.addUrlParam(window.location.search, 'page');
        base.i++;
        fetch(url)
          .then(function (response) {
            return response.text();
          })
          .then(function (html) {
            var result = base.appendProducts(html, true);
            if (result === 'end') {
              base.finishRestore(state);
              return;
            }
            step();
          })
          .catch(function () {
            base.finishRestore(state);
          });
      };

      step();
    }

    finishRestore(state) {
      var base = this;
      base.restoring = false;
      base.removeAttribute('loading');
      window.requestAnimationFrame(function () {
        window.scrollTo(0, state.scrollY || 0);
        base.autoLoads = 0;
        base.observeForAutoLoad();
      });
    }

    /* ---------- loading ---------- */

    observeForAutoLoad() {
      var base = this;
      if (base.atEnd) return;
      if (base.autoLoads >= base.autoLimit) return;
      if (!('IntersectionObserver' in window)) return;

      base.observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            if (base.restoring || base.atEnd) return;
            if (base.autoLoads >= base.autoLimit) {
              base.stopObserving();
              return;
            }
            base.autoLoads++;
            base.loadProducts();
            if (base.autoLoads >= base.autoLimit) base.stopObserving();
          });
        },
        { rootMargin: '200px 0px' }
      );
      base.observer.observe(base);
    }

    stopObserving() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }

    addUrlParam(search, key) {
      var newParam = key + '=' + this.i;
      var params = '?' + newParam;

      if (search) {
        params = search.replace(new RegExp('([?&])' + key + '[^&]*'), '$1' + newParam);
        if (params === search) {
          params += '&' + newParam;
        }
      }

      return params;
    }

    loadProducts() {
      var base = this;
      if (base.atEnd || base.getAttribute('loading')) return;
      base.setAttribute('loading', true);
      base.fetchNext(function () {
        base.removeAttribute('loading');
      });
    }

    fetchNext(done) {
      var base = this;
      var url = window.location.pathname + base.addUrlParam(window.location.search, 'page');
      base.i++;

      fetch(url)
        .then(function (response) {
          return response.text();
        })
        .then(function (html) {
          var result = base.appendProducts(html, false);

          if (typeof dispatchCustomEvent === 'function') {
            dispatchCustomEvent('pagination:page-changed', { url: url });
          }

          if (result === 'added') {
            base.emptyStreak = 0;
            done();
            return;
          }

          // A page can render nothing if every product on it is sold out,
          // which this theme hides. Skip ahead rather than stopping dead.
          if (result === 'empty' && base.emptyStreak < MAX_EMPTY_PAGES) {
            base.emptyStreak++;
            base.fetchNext(done);
            return;
          }

          base.markEnd();
          done();
        })
        .catch(function () {
          // Network hiccup. Leave the button in place so they can try again.
          done();
        });
    }

    appendProducts(html, immediate) {
      var base = this;
      var doc;

      try {
        doc = new DOMParser().parseFromString(html, 'text/html');
      } catch (e) {
        return 'end';
      }

      var nextGrid = doc.getElementById('product-grid');
      if (!nextGrid) return 'end';

      var products = nextGrid.querySelectorAll('.column');
      if (!products.length) return 'empty';

      for (var n = 0; n < products.length; n++) {
        base.grid.appendChild(products[n]);
      }

      if (!immediate && base.animations_enabled) {
        gsap.set(products, { opacity: 0, y: 30 });
        gsap.to(products, {
          duration: 0.5,
          y: 0,
          opacity: 1,
          stagger: 0.05,
          onComplete: function () {
            base.refreshBadges();
          }
        });
      } else {
        base.refreshBadges();
      }

      return 'added';
    }

    markEnd() {
      this.atEnd = true;
      this.stopObserving();
      if (this.button) this.button.classList.add('visually-hidden');
    }

    refreshBadges() {
      // Stamped reviews app, if it is installed on the page.
      if (typeof StampedFn !== 'undefined' && typeof StampedFn.loadBadges === 'function') {
        StampedFn.loadBadges();
      }
    }
  }

  customElements.define('pagination-theme', Pagination);
}
